import nacl from 'tweetnacl';
import type { VideoMetadata, Pin, UserProfile, FollowPayload } from './metadata';
import type { Community, CommunityMember, CommunityPin } from './communities';

function arrayBufferToBase64(buffer: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < buffer.byteLength; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function ensureUint8Array(data: ArrayLike<number> | ArrayBufferLike): Uint8Array {
  if (data instanceof Uint8Array) return data;
  if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  return new Uint8Array(data);
}

export type EventType =
  | 'video'
  | 'pin'
  | 'profile'
  | 'community'
  | 'delete_community'
  | 'community_member'
  | 'community_pin'
  | 'follow'
  | 'unfollow'
  | 'community_create'
  | 'community_join'
  | 'community_leave'
  | 'community_lock'
  | 'community_unlock'
  | 'channel_create'
  | 'channel_delete'
  | 'channel_message'
  | 'community_post'
  | 'mod_grant'
  | 'mod_revoke'
  | 'community_ban'
  | 'community_unban'
  | 'community_pin_post'
  | 'community_invite'
  | 'post'
  | 'delete_post'
  | 'delete_video'
  | 'event_listing'
  | 'delete_event_listing'
  | 'event_rsvp'
  | 'comment'
  | 'delete_comment'
  | 'listing'
  | 'delete_listing'
  | 'dm'
  | 'like'
  | 'content_report'
  | 'pin_post'
  | 'unpin_post';

export interface PostPayload {
  content: string;
  city?: string;
  magnetURI?: string;
  mediaType?: 'image' | 'audio' | 'video';
  replyTo?: { pubkey: string; postId: string };
}

export interface EventListingPayload {
  id: string;
  title: string;
  description: string;
  startTime: number;
  endTime?: number;
  city: string;
  country?: string;
  venue?: string;
  tags?: string[];
  links?: Array<{ label: string; url: string }>;
  communityId?: string;
  flyerMagnetURI?: string;
  flyerType?: 'image' | 'video';
}

export interface DeleteEventListingPayload {
  listingId: string;
  pubkey: string;
}

export interface DeleteCommunityPayload {
  communityId: string;
  userId: string;
}

export interface EventRSVPPayload {
  eventId: string;
  status: 'going' | 'interested';
}

export interface CommentPayload {
  videoHash: string;
  userId: string;
  username: string;
  text: string;
}

export interface DeleteCommentPayload {
  commentId: string;
  userId: string;
}

export interface DeletePostPayload {
  postId: string;
  pubkey: string;
}

export interface DeleteVideoPayload {
  hash: string;
  userId: string;
}

export interface ListingPayload {
  userId: string;
  username: string;
  title: string;
  description: string;
  price: string;
  paymentUrl: string;
  category: string;
}

export interface DeleteListingPayload {
  listingId: string;
  userId: string;
}

export interface ContentReportPayload {
  targetId: string;
  targetType: 'post' | 'video' | 'event_listing' | 'community' | 'listing';
  reason: 'spam' | 'harassment' | 'illegal' | 'other';
  note?: string;
}

export interface PinPostPayload {
  postId: string;
  pubkey: string;
}

export interface UnpinPostPayload {
  postId: string;
  pubkey: string;
}

export interface LikePayload {
  targetId: string;
  targetType: 'video' | 'post' | 'channel_message';
  userId: string;
}

export interface DMPayload {
  toUserId: string;
  fromUserId: string;
  fromUsername: string;
  fromBoxPubkey: string;
  nonce: string;
  ciphertext: string;
}

export type EventPayload =
  | VideoMetadata
  | Pin
  | UserProfile
  | Community
  | CommunityMember
  | CommunityPin
  | FollowPayload
  | PostPayload
  | DeletePostPayload
  | DeleteVideoPayload
  | DeleteEventListingPayload
  | DeleteCommunityPayload
  | CommentPayload
  | DeleteCommentPayload
  | ListingPayload
  | DeleteListingPayload
  | DMPayload
  | LikePayload
  | ContentReportPayload
  | PinPostPayload
  | UnpinPostPayload;

export interface AuthEvent {
  id: string;
  pubkey: string;
  type: EventType;
  payload: EventPayload;
  timestamp: number;
  sig: string;
}

function sortKeys(obj: unknown): unknown {
  if (typeof obj !== 'object' || obj === null) return obj;
  if (Array.isArray(obj)) return (obj as unknown[]).map(sortKeys);
  return Object.keys(obj as Record<string, unknown>)
    .sort()
    .reduce((acc: Record<string, unknown>, key) => {
      acc[key] = sortKeys((obj as Record<string, unknown>)[key]);
      return acc;
    }, {});
}

export async function buildEventId(
  pubkey: string,
  type: EventType,
  timestamp: number,
  payload: EventPayload
): Promise<string> {
  const canonical = JSON.stringify({ pubkey, type, timestamp, payload: sortKeys(payload) });
  const encoded = new TextEncoder().encode(canonical);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function createEvent(
  type: EventType,
  payload: EventPayload,
  pubkey: string,
  privateKey: string
): Promise<AuthEvent> {
  const timestamp = Date.now();
  const id = await buildEventId(pubkey, type, timestamp, payload);
  const secretKey = base64ToArrayBuffer(privateKey);
  const idBytes = ensureUint8Array(new TextEncoder().encode(id));
  const sig = arrayBufferToBase64(nacl.sign.detached(idBytes, secretKey));
  return { id, pubkey, type, payload, timestamp, sig };
}

export async function verifyEvent(event: AuthEvent): Promise<boolean> {
  try {
    const expectedId = await buildEventId(event.pubkey, event.type, event.timestamp, event.payload);
    if (expectedId !== event.id) return false;
    const pubKeyBytes = base64ToArrayBuffer(event.pubkey);
    const idBytes = ensureUint8Array(new TextEncoder().encode(event.id));
    const sigBytes = base64ToArrayBuffer(event.sig);
    return nacl.sign.detached.verify(idBytes, sigBytes, pubKeyBytes);
  } catch {
    return false;
  }
}
